import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Card, CardHeader, Grid, IconButton, Stack, Switch, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShieldIcon from '@mui/icons-material/Shield';
import GroupIcon from '@mui/icons-material/Group';
import CloudIcon from '@mui/icons-material/Cloud';
import PanToolIcon from '@mui/icons-material/PanTool';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { useSnackbar } from 'notistack';
import PageTitle from '../../components/PageTitle';
import KpiCard from './shared/KpiCard';
import { apiConstants, getGeneric, postGeneric, putGeneric } from '../../common/constants';
import { CLOSURE_CATEGORIES, CLOSURE_CATEGORY_META } from './shared/constants';
import { invalidateAppointmentConfig } from './shared/useAppointmentConfig';
import PageBanner from './shared/PageBanner';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const CATEGORY_ICONS = {
    [CLOSURE_CATEGORIES.PROZO]: <ShieldIcon />,
    [CLOSURE_CATEGORIES.NON_PROZO]: <GroupIcon />,
    [CLOSURE_CATEGORIES.FORCE_MAJEURE]: <CloudIcon />,
    [CLOSURE_CATEGORIES.JOINT]: <PanToolIcon />,
};

function ReasonSection({
    title, subtitle, items, accent, addCodePrefix, onAdd, onToggleActive, onUpdateLabel, onValidationError,
}) {
    const [newCode, setNewCode] = useState('');
    const [newLabel, setNewLabel] = useState('');

    const submit = () => {
        const code = newCode.trim();
        const label = newLabel.trim();
        if (!code || !label) {
            onValidationError('Code and Display Label are both required');
            return;
        }
        onAdd(code, label);
        setNewCode('');
        setNewLabel('');
    };

    return (
        <Card sx={{ borderTop: `3px solid ${accent}` }}>
            <CardHeader
                title={<Typography fontWeight={700} sx={{ color: accent }}>{title}</Typography>}
                subheader={subtitle}
            />
            <TableContainer>
                <Table size='small'>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell sx={{ width: 40 }}>#</TableCell>
                            <TableCell><strong>Code</strong></TableCell>
                            <TableCell><strong>Display Label (KAM-facing)</strong></TableCell>
                            <TableCell align='center' sx={{ width: 80 }}><strong>Active</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.length === 0 && (
                            <TableRow><TableCell colSpan={4} align='center' sx={{ color: 'text.secondary', py: 2 }}>No reasons configured</TableCell></TableRow>
                        )}
                        {items.map((r, idx) => (
                            <TableRow key={r.id || r.code} hover>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>
                                    <Typography variant='caption' fontFamily='monospace'>{r.code}</Typography>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size='small' variant='standard' fullWidth
                                        defaultValue={r.label}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v && v !== r.label) onUpdateLabel(r.id, v);
                                        }}
                                    />
                                </TableCell>
                                <TableCell align='center'>
                                    <Switch
                                        checked={r.isActive !== false}
                                        onChange={(e) => onToggleActive(r.id, e.target.checked)}
                                        size='small'
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ p: 2, borderTop: '2px dashed #ddd', bgcolor: '#fafafa' }}>
                <Typography variant='caption' fontWeight={700} sx={{ color: accent, display: 'block', mb: 1 }}>
                    Add new reason
                </Typography>
                <Stack spacing={1}>
                    <TextField
                        size='small' fullWidth placeholder={`Code (e.g., ${addCodePrefix}_NEW_REASON)`}
                        value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                        inputProps={{ style: { fontFamily: 'monospace' } }}
                    />
                    <TextField
                        size='small' fullWidth placeholder='Display label — what KAMs see'
                        value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                    />
                    <Button
                        variant='contained' size='small' startIcon={<AddIcon />}
                        sx={{ alignSelf: 'flex-start', bgcolor: accent, '&:hover': { bgcolor: accent, opacity: 0.9 } }}
                        onClick={submit}
                    >Add Reason</Button>
                </Stack>
            </Box>
        </Card>
    );
}

export default function MissReasonSettings() {
    const { enqueueSnackbar } = useSnackbar();
    const [reasons, setReasons] = useState([]);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await getGeneric(apiConstants.APPOINTMENT_DELIVERY_CLOSURE_REASON, {});
                const list = Array.isArray(res?.data) ? res.data : (res?.data?.responseObj || []);
                setReasons(list);
            } catch (e) {
                setReasons([]);
                enqueueSnackbar(
                    e?.response?.data?.message || 'Failed to load closure reasons',
                    { variant: 'error' },
                );
            }
        };
        load();
    }, [reload, enqueueSnackbar]);

    const byCategory = useMemo(() => CLOSURE_CATEGORY_META.reduce(
        (acc, m) => ({ ...acc, [m.value]: reasons.filter((r) => r.category === m.value) }), {},
    ), [reasons]);

    const totalActive = reasons.filter((r) => r.isActive !== false).length;
    const totalInactive = reasons.filter((r) => r.isActive === false).length;

    const handleAdd = async (category, code, label) => {
        try {
            await postGeneric(apiConstants.APPOINTMENT_DELIVERY_CLOSURE_REASON, { code, label, category, isActive: true });
            enqueueSnackbar('Reason added', { variant: 'success' });
            invalidateAppointmentConfig();
            setReload((r) => r + 1);
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || 'Failed to add reason. Admin role required.', { variant: 'error' });
        }
    };

    const handleToggleActive = async (id, isActive) => {
        try {
            await putGeneric(`${apiConstants.APPOINTMENT_DELIVERY_CLOSURE_REASON}/${id}`, { isActive });
            enqueueSnackbar(`Reason ${isActive ? 'enabled' : 'disabled'}`, { variant: 'success' });
            invalidateAppointmentConfig();
            setReload((r) => r + 1);
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || 'Failed to update', { variant: 'error' });
        }
    };

    const handleUpdateLabel = async (id, label) => {
        try {
            await putGeneric(`${apiConstants.APPOINTMENT_DELIVERY_CLOSURE_REASON}/${id}`, { label });
            enqueueSnackbar('Label updated', { variant: 'success' });
            invalidateAppointmentConfig();
            setReload((r) => r + 1);
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || 'Failed to update label', { variant: 'error' });
        }
    };

    return (
        <>
            <PageTitle>Miss Reason Settings</PageTitle>

            <Stack direction='row' justifyContent='flex-end' sx={{ mb: 1 }}>
                <IconButton onClick={() => setReload((r) => r + 1)}><RefreshIcon /></IconButton>
            </Stack>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                {CLOSURE_CATEGORY_META.map((m) => (
                    <Grid item xs={12} sm={6} md={2} key={m.value}>
                        <KpiCard color={m.kpiColor} label={`${m.label} Reasons`}
                            value={byCategory[m.value].filter((r) => r.isActive !== false).length}
                            sub='Active sub-causes' icon={CATEGORY_ICONS[m.value]} />
                    </Grid>
                ))}
                <Grid item xs={12} sm={6} md={2}>
                    <KpiCard color='green' label='Total Active Reasons' value={totalActive}
                        sub='Across all categories' icon={<CheckCircleIcon />} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <KpiCard color='grey' label='Inactive Reasons' value={totalInactive}
                        sub='Hidden from KAM dropdown' icon={<BlockIcon />} />
                </Grid>
            </Grid>

            <PageBanner variant='amber' icon={<HelpOutlineIcon />} title="Why can't I delete a reason?">
                Deleting a miss reason would break historical data — past appointments marked with that reason would lose their reference.
                Use <strong>Active toggle</strong> to hide from the dropdown. Inactive reasons still resolve in historical records.
            </PageBanner>

            <Grid container spacing={2}>
                {CLOSURE_CATEGORY_META.map((m) => (
                    <Grid item xs={12} md={6} key={m.value}>
                        <ReasonSection
                            title={`${m.label} Reasons`}
                            subtitle={m.sectionSubtitle}
                            items={byCategory[m.value]}
                            accent={m.color}
                            addCodePrefix={m.codePrefix}
                            onAdd={(code, label) => handleAdd(m.value, code, label)}
                            onToggleActive={handleToggleActive}
                            onUpdateLabel={handleUpdateLabel}
                            onValidationError={(msg) => enqueueSnackbar(msg, { variant: 'warning' })}
                        />
                    </Grid>
                ))}
            </Grid>
        </>
    );
}
